import type { Icon as PhosphorIcon } from '@phosphor-icons/react';
import { ICON_SET, type GeneratedIconName } from './icon-set.generated.js';
import { icon as iconTokens } from '../tokens.js';
import { cx } from './cx.js';
import { passthrough } from './passthrough.js';

/**
 * One icon set, one weight.
 *
 * Phosphor at `light`. Two weights read as two icon sets, so the weight is a
 * token rather than a prop, and the stroke is not overridden at all - light is
 * already 1.25px at 18px, and forcing a width on top of it produces a glyph
 * that is neither this weight nor another.
 *
 * An icon is either decorative - in which case it is hidden from assistive
 * technology because the text beside it already says what it is - or it is the
 * only thing there, in which case it must carry a label. The type makes that
 * choice explicit rather than optional.
 */

/**
 * The set, addressed by name.
 *
 * Generated from the names the source actually uses. The package barrel would
 * have worked and would have shipped all 1,512 icons - four times the bundle,
 * on a product whose own tracker notes that bundle size is the first thing
 * that bites on a phone with one bar.
 */
const iconSet: Record<string, unknown> = ICON_SET;

/**
 * The names that draw something.
 *
 * Typed so the compiler refuses a name the product does not ship. It was the
 * whole Phosphor export list; it is now the generated set, which is narrower
 * and more useful - a name nobody has generated an import for would render
 * nothing, and the type says so before the build does.
 *
 * Adding an icon is: write the name, run `pnpm generate`. That is the same
 * contract theme.generated.css has, and it is what keeps the bundle to the
 * icons in use.
 *
 * `ExternalLink` was passed POSITIONALLY to a field renderer - not through an
 * `icon=` prop - so check:icons walked past it and reported every name present
 * while every URL field would have drawn no glyph. A grep finds the shapes
 * somebody thought of; a type finds all of them.
 */
export type IconName = GeneratedIconName;

type IconProps = {
  name: string;
  /**
   * `default` is the working size, in a nav rail or a toolbar. `inline` sits
   * in a line of text. `feature` is for the two places an icon is the subject
   * rather than the label: an empty state and a page header.
   */
  size?: 'default' | 'inline' | 'feature';
  className?: string;
} & (
  | {
      /** The text beside it already names it. */
      decorative: true;
      label?: never;
    }
  | {
      decorative?: false;
      /** What it means, for somebody who cannot see it. */
      label: string;
    }
);

/**
 * Icons that say "that way" and must turn around in a mirrored layout.
 *
 * Not everything with a direction in its name belongs here. `ArrowUp` and
 * `ArrowDown` mean up and down in every language; `TrendUp` means growth, and
 * mirroring it would say decline.
 */
export const DIRECTIONAL_ICONS: ReadonlySet<string> = new Set([
  'ArrowLeft',
  'ArrowRight',
  'ArrowUpRight',
  'ArrowDownRight',
  'ArrowUpLeft',
  'ArrowDownLeft',
  'ArrowBendUpLeft',
  'ArrowBendUpRight',
  'ArrowBendDownLeft',
  'ArrowBendDownRight',
  'ArrowUUpLeft',
  'ArrowUUpRight',
  'ArrowLineLeft',
  'ArrowLineRight',
  'ArrowFatLeft',
  'ArrowFatRight',
  'CaretLeft',
  'CaretRight',
  'CaretDoubleLeft',
  'CaretDoubleRight',
  'CaretLineLeft',
  'CaretLineRight',
  'PaperPlaneTilt',
  'PaperPlaneRight',
  'SignIn',
  'SignOut',
  // The rail slides in from the start edge, which is the right in Arabic.
  'Sidebar',
  'SidebarSimple',
  'TextIndent',
  'TextOutdent',
  'SkipForward',
  'SkipBack',
  // A switch's off position sits at the start of the track, which is the right
  // in Arabic. Found by the check rather than by looking, which is the point
  // of having it.
  'ToggleLeft',
  'ToggleRight',
]);

/** True when this icon says "that way" and must turn around in a mirrored layout. */
export function isDirectional(name: string): boolean {
  return DIRECTIONAL_ICONS.has(name);
}

function lookupIcon(name: string): PhosphorIcon | undefined {
  const found = iconSet[name];
  // A Phosphor icon is the result of `forwardRef`, which is an OBJECT carrying
  // a `$$typeof` tag - not a function. A `typeof === 'function'` guard here
  // rejected every icon in the set while `iconExists` went on answering `name
  // in iconSet`, so the check reported 75 of 75 present and the component
  // rendered none of them. Two answers to one question (P-1), so there is now
  // one: `iconExists` asks this function.
  if (typeof found === 'function') return found as PhosphorIcon;
  if (typeof found === 'object' && found !== null && '$$typeof' in found) {
    return found as PhosphorIcon;
  }
  return undefined;
}

/**
 * Says a name is missing, once, without assuming it is in Node.
 *
 * This was `process.env['NODE_ENV'] !== 'production'`. `process` does not
 * exist in a browser, so the guard THREW - and React unmounted the whole
 * screen into an error boundary. The comment below promised "a missing icon
 * renders nothing rather than a placeholder shape"; what it actually did was
 * take the page down, and because jsdom has `process`, no test could see it.
 *
 * `typeof` first, because that is the operator that is safe on an undeclared
 * identifier. Once per name, because a missing icon in a list of two hundred
 * rows is two hundred identical lines.
 */
const warned = new Set<string>();
function warnOnce(name: string): void {
  if (warned.has(name)) return;
  warned.add(name);
  const production =
    typeof process !== 'undefined' && process.env?.['NODE_ENV'] === 'production';
  if (!production) {
    console.error(`[Icon] "${name}" is not in the icon set. Nothing was rendered in its place.`);
  }
}

const SIZES = {
  default: iconTokens.default,
  inline: iconTokens.inline,
  feature: iconTokens.feature,
} as const;

export function Icon({ name, size = 'default', className, ...rest }: IconProps): JSX.Element | null {
  const Component = lookupIcon(name);
  if (!Component) {
    // A missing icon renders nothing rather than a placeholder shape. A
    // placeholder is a lie that survives review; an absence is noticed. This
    // matters more with Phosphor than it did before: a name the set does not
    // have is an empty box, not an error, so check:icons is the only thing
    // standing between a rename and a toolbar of blanks.
    warnOnce(name);
    return null;
  }
  const dimension = SIZES[size];
  const decorative = 'decorative' in rest && rest.decorative === true;
  return (
    <Component
      width={dimension}
      height={dimension}
      weight={iconTokens.weight}
      // Mirrored by the layout direction rather than by a prop. A caller who
      // had to remember to pass `mirror` would forget, and would forget on the
      // screen nobody opens in Arabic.
      data-directional={isDirectional(name) ? 'true' : undefined}
      className={cx(className, isDirectional(name) && 'rtl:-scale-x-100')}
      aria-hidden={decorative ? true : undefined}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : (rest as { label: string }).label}
      focusable="false"
      {...passthrough(rest as Record<string, unknown>)}
    />
  );
}

/**
 * Whether an icon renders.
 *
 * Asks `lookupIcon`, so this and the component cannot come to disagree about
 * what "in the icon set" means. `check:icons` calls this, and the point of the
 * check is that it answers the same question the screen does.
 */
export function iconExists(name: string): boolean {
  return lookupIcon(name) !== undefined;
}
